import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useApi } from '../lib/hooks';
import { useAuth } from '../lib/auth';
import { initials } from '../lib/format';
import type { Campaign } from '../lib/api';
import { Badge, cx } from './ui';
import { CommandPalette } from './CommandPalette';
import { AIBudgetBanner } from './AIBudget';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? navigator.userAgent);

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/today', label: 'Today' },
  { to: '/prospects', label: 'Prospects' },
  { to: '/companies', label: 'Companies' },
  { to: '/pipeline', label: 'Pipeline' },
  { to: '/queue', label: 'Review queue' },
  { to: '/replies', label: 'Replies' },
  { to: '/campaigns', label: 'Campaigns' },
  { to: '/posts', label: 'LinkedIn' },
  { to: '/analytics', label: 'Analytics' },
  { to: '/ask', label: 'Ask' },
  { to: '/ai-usage', label: 'AI Usage' },
  // Admin-only: /api/gmail/* rejects anyone else, same as the settings this
  // used to live inside before it got its own tab.
  { to: '/gmail-contacts', label: 'Gmail', adminOnly: true },
  { to: '/settings', label: 'Settings' },
];

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const campaigns = useApi<{ campaigns: Campaign[] }>('/api/campaigns');
  const queue = useApi<{ queue: unknown[] }>('/api/outreach/queue?limit=200');

  const pending = queue.data?.queue.length ?? 0;
  const visibleNav = NAV.filter((item) => !item.adminOnly || user?.role === 'admin');

  return (
    <div className="min-h-screen bg-ink-50 text-ink-900">
      <AIBudgetBanner />
      <header className="sticky top-0 z-30 border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-[100rem] items-center gap-4 px-4 py-2.5">
          <button
            className="rounded p-1.5 text-ink-500 hover:bg-ink-100 lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
              <path d="M3 5.5A.75.75 0 013.75 4.75h12.5a.75.75 0 010 1.5H3.75A.75.75 0 013 5.5zm0 4.5a.75.75 0 01.75-.75h12.5a.75.75 0 010 1.5H3.75A.75.75 0 013 10zm.75 3.75a.75.75 0 000 1.5h12.5a.75.75 0 000-1.5H3.75z" />
            </svg>
          </button>

          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-left">
            <span className="flex h-7 w-7 items-center justify-center rounded bg-accent-600 text-xs font-bold text-white">
              O
            </span>
            <span className="text-sm font-semibold tracking-tight">Outreach</span>
          </button>

          <nav className="hidden flex-1 items-center gap-0.5 lg:flex">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cx(
                    'relative rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    isActive ? 'bg-ink-100 text-ink-900' : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900',
                  )
                }
              >
                {item.label}
                {item.to === '/queue' && pending > 0 && (
                  <span className="ml-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-accent-600 px-1 text-[11px] font-semibold leading-5 text-white">
                    {pending}
                  </span>
                )}
              </NavLink>
            ))}
            <NavLink
              to="/guides"
              className={({ isActive }) =>
                cx(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  isActive ? 'bg-ink-100 text-ink-900' : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900',
                )
              }
            >
              Guides
            </NavLink>
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => document.dispatchEvent(new CustomEvent('command-palette:open'))}
              className="hidden items-center gap-1.5 rounded-md border border-ink-200 px-2.5 py-1 text-xs text-ink-500 hover:bg-ink-50 sm:flex"
              aria-label="Open command palette"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                <path
                  fillRule="evenodd"
                  d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                  clipRule="evenodd"
                />
              </svg>
              Search
              <kbd className="rounded border border-ink-200 bg-ink-50 px-1 text-[10px]">
                {isMac ? '⌘K' : 'Ctrl K'}
              </kbd>
            </button>
            <span className="hidden text-xs text-ink-500 sm:inline">
              {campaigns.data?.campaigns.filter((c) => c.status === 'active').length ?? 0} active campaigns
            </span>
            <div className="group relative">
              <button className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-800 text-xs font-semibold text-white">
                {initials(user?.full_name || user?.email || '?')}
              </button>
              <div className="invisible absolute right-0 top-full z-40 w-56 pt-2 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <div className="rounded-md bg-white p-1 shadow-lg ring-1 ring-ink-200">
                  <div className="px-3 py-2">
                    <p className="truncate text-sm font-medium text-ink-900">{user?.full_name || 'Account'}</p>
                    <p className="truncate text-xs text-ink-500">{user?.email}</p>
                    <div className="mt-1.5">
                      <Badge tone={user?.role === 'admin' ? 'blue' : 'neutral'}>{user?.role}</Badge>
                    </div>
                  </div>
                  <button
                    onClick={() => void logout()}
                    className="w-full rounded px-3 py-2 text-left text-sm text-ink-700 hover:bg-ink-50"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {menuOpen && (
          <nav className="grid gap-0.5 border-t border-ink-100 px-3 pb-3 pt-2 lg:hidden">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  cx(
                    'rounded-md px-3 py-2 text-sm font-medium',
                    isActive ? 'bg-ink-100 text-ink-900' : 'text-ink-600',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/guides"
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                cx('rounded-md px-3 py-2 text-sm font-medium', isActive ? 'bg-ink-100 text-ink-900' : 'text-ink-600')
              }
            >
              Guides
            </NavLink>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-[100rem] px-4 py-6">
        <Outlet />
      </main>

      <VersionFooter />
      <CommandPalette />
    </div>
  );
}

interface HealthResponse {
  ok: boolean;
  version?: {
    commit: string | null;
    commitShort: string | null;
    commitDate: string | null;
    commitSubject: string | null;
    branch: string | null;
  };
}

function VersionFooter() {
  const health = useApi<HealthResponse>('/api/health');
  const version = health.data?.version;
  if (!version?.commitShort) return null;

  const commitDate = version.commitDate ? new Date(version.commitDate).toLocaleString() : null;

  return (
    <footer className="group relative mx-auto max-w-[100rem] px-4 pb-4 text-right">
      <span className="cursor-default text-[11px] text-ink-400">
        {version.branch ? `${version.branch}@` : ''}
        {version.commitShort}
        {commitDate ? ` · ${commitDate}` : ''}
      </span>
      {version.commitSubject && (
        <div className="invisible absolute bottom-full right-4 z-40 mb-1 w-max max-w-sm rounded-md bg-ink-900 px-2.5 py-1.5 text-left text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100">
          <p className="font-mono text-[11px] text-ink-300">{version.commit}</p>
          <p className="mt-0.5">{version.commitSubject}</p>
          {commitDate && <p className="mt-0.5 text-ink-400">{commitDate}</p>}
        </div>
      )}
    </footer>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  helpHref,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** This page's knowledgebase article slug (matches web/public/kb/<slug>.html), e.g. "campaigns". */
  helpHref?: string;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold tracking-tight text-ink-900">{title}</h1>
          {helpHref && (
            <NavLink
              to={`/guides/${helpHref}`}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-accent-700 hover:bg-accent-50"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM9.25 9a.75.75 0 001.5 0c0-.414.336-.75.75-.75.69 0 1.25.56 1.25 1.25 0 .479-.267.845-.71 1.08-.545.288-1.29.828-1.29 1.92a.75.75 0 001.5 0c0-.397.166-.59.577-.806C13.72 10.99 14.25 10.2 14.25 9.25 14.25 7.732 13.018 6.5 11.5 6.5 10.12 6.5 9 7.62 9 9z"
                  clipRule="evenodd"
                />
                <circle cx="10" cy="14" r="1" />
              </svg>
              Guide
            </NavLink>
          )}
        </div>
        {description && <p className="mt-0.5 text-sm text-ink-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
